import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, EyeOff, Lock } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { updateNewPassword } from '../../store/thunk/authThunk';
import { selectAuthLoading } from '../../store/reducer/authSlice';

const UserReSetPassword = () => {
    const [formData, setFormData] = useState({
        new_password: '',
        confirm_password: '',
    });
    const [showPasswords, setShowPasswords] = useState({
        new: false,
        confirm: false,
    });
    const [errors, setErrors] = useState({});

    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const loading = useSelector(selectAuthLoading);

    const { user_id } = location.state || {};

    // Redirect if user_id is missing
    useEffect(() => {
        if (!user_id) {
            toast.error('Invalid reset session. Please try again.');
            navigate('/signin');
        }
    }, [user_id, navigate]);

    const validateForm = () => {
        const newErrors = {};

        // New password validation
        if (!formData.new_password) {
            newErrors.new_password = 'New password is required';
        } else if (formData.new_password.length < 6) {
            newErrors.new_password = 'Password must be at least 6 characters';
        }

        // Confirm password validation
        if (!formData.confirm_password) {
            newErrors.confirm_password = 'Please confirm your new password';
        } else if (formData.new_password !== formData.confirm_password) {
            newErrors.confirm_password = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Clear specific error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: '',
            }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            // Show first error as toast
            const firstError = Object.values(errors)[0];
            if (firstError) {
                toast.error(firstError);
            }
            return;
        }

        try {
            // Reset password (after OTP verification)
            const payload = {
                user_id: String(user_id),
                password: formData.new_password,
                confirm_password: formData.confirm_password,
            };
            const response = await dispatch(updateNewPassword(payload)).unwrap();

            if (response?.status === 1) {
                toast.success(response.message || 'Password updated successfully!');

                // Reset form
                setFormData({
                    new_password: '',
                    confirm_password: '',
                });
                setErrors({});

                toast.success(
                    'Password reset successfully! You can now login with your new password.',
                );

                // Navigate to signin
                navigate('/signin');
            } else {
                toast.error(response?.message || 'Failed to update password. Please try again.');
            }
        } catch (err) {
            console.error('Password update error:', err);
            toast.error(err.message || 'Failed to update password. Please try again.');
        }
    };

    return (
        <div className='page_bg min-h-screen flex flex-col relative overflow-hidden'>
            {/* Header */}
            <div className='flex items-center p-4 pt-8'>
                <button onClick={() => navigate('/')} disabled={loading}>
                    <ArrowLeft className='text-white w-6 h-6' />
                </button>
            </div>

            {/* Logo */}
            <div className='flex justify-center mt-4 mb-16'>
                <img
                    src='https://images.halalfull.com/assets/img/logo.svg'
                    alt='Logo'
                    className='w-36'
                />
            </div>

            {/* Header with Icon */}
            <div className='px-6 mb-8'>
                <div className='flex items-center space-x-2 mb-2'>
                    <Lock className='w-6 h-6 text-white' />
                    <h2 className='text-white text-xl font-semibold'>Set New Password</h2>
                </div>
                <p className='text-white text-sm opacity-80'>
                    Create a secure password for your account
                </p>
            </div>

            {/* Form */}
            <div className='flex-1 px-6'>
                <form onSubmit={handleSubmit} className='space-y-6'>
                    {/* New Password */}
                    <div>
                        <label className='block text-white text-sm font-medium mb-2'>
                            New Password
                        </label>
                        <div className='relative'>
                            <input
                                type={showPasswords.new ? 'text' : 'password'}
                                name='new_password'
                                value={formData.new_password}
                                onChange={handleInputChange}
                                disabled={loading}
                                className={`w-full pr-10 py-3 bg-transparent border-b-2 text-white focus:outline-none transition-colors placeholder-white placeholder-opacity-40 ${
                                    errors.new_password
                                        ? 'border-red-300 focus:border-red-500'
                                        : 'border-white border-opacity-60 focus:border-opacity-100'
                                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                placeholder='Enter your new password'
                                autoComplete='new-password'
                            />
                            <button
                                type='button'
                                onClick={() => togglePasswordVisibility('new')}
                                disabled={loading}
                                className='absolute right-2 top-1/2 transform -translate-y-1/2 text-white opacity-60 hover:opacity-100 transition-opacity'
                            >
                                {showPasswords.new ? (
                                    <EyeOff className='w-5 h-5' />
                                ) : (
                                    <Eye className='w-5 h-5' />
                                )}
                            </button>
                        </div>
                        {errors.new_password && (
                            <p className='text-red-400 text-xs mt-1'>{errors.new_password}</p>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className='block text-white text-sm font-medium mb-2'>
                            Confirm New Password
                        </label>
                        <div className='relative'>
                            <input
                                type={showPasswords.confirm ? 'text' : 'password'}
                                name='confirm_password'
                                value={formData.confirm_password}
                                onChange={handleInputChange}
                                disabled={loading}
                                className={`w-full pr-10 py-3 bg-transparent border-b-2 text-white focus:outline-none transition-colors placeholder-white placeholder-opacity-40 ${
                                    errors.confirm_password
                                        ? 'border-red-300 focus:border-red-500'
                                        : formData.new_password &&
                                          formData.confirm_password &&
                                          formData.new_password === formData.confirm_password
                                        ? 'border-green-400 focus:border-green-500'
                                        : 'border-white border-opacity-60 focus:border-opacity-100'
                                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                placeholder='Confirm your new password'
                                autoComplete='new-password'
                            />
                            <button
                                type='button'
                                onClick={() => togglePasswordVisibility('confirm')}
                                disabled={loading}
                                className='absolute right-2 top-1/2 transform -translate-y-1/2 text-white opacity-60 hover:opacity-100 transition-opacity'
                            >
                                {showPasswords.confirm ? (
                                    <EyeOff className='w-5 h-5' />
                                ) : (
                                    <Eye className='w-5 h-5' />
                                )}
                            </button>
                        </div>
                        {errors.confirm_password && (
                            <p className='text-red-400 text-xs mt-1'>{errors.confirm_password}</p>
                        )}

                        {/* Password Match Status */}
                        {formData.new_password && formData.confirm_password && (
                            <p
                                className={`text-xs mt-1 ${
                                    formData.new_password === formData.confirm_password
                                        ? 'text-green-400'
                                        : 'text-red-400'
                                }`}
                            >
                                {formData.new_password === formData.confirm_password
                                    ? '✓ Passwords match'
                                    : '✗ Passwords do not match'}
                            </p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <div className='mt-8'>
                        <button
                            type='submit'
                            disabled={loading || Object.keys(errors).length > 0}
                            className={`w-full py-3 px-4 rounded-full font-semibold text-base shadow-[1px_1px_6px_0_#000000c4] transition-colors ${
                                loading || Object.keys(errors).length > 0
                                    ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                                    : 'bg-red-800 text-white hover:bg-red-700'
                            }`}
                        >
                            {loading ? 'Setting New Password...' : 'Set New Password'}
                        </button>
                    </div>

                    {/* Terms */}
                    <p className='text-xs text-white text-opacity-60 text-center mt-4 leading-relaxed'>
                        By updating your password, you agree to our{' '}
                        <a href='/terms-conditions' className='text-red-400 hover:underline'>
                            Terms of Service
                        </a>{' '}
                        and{' '}
                        <a href='/privacy-policy' className='text-red-400 hover:underline'>
                            Privacy Policy
                        </a>
                        .
                    </p>
                </form>
            </div>
        </div>
    );
};

export default UserReSetPassword;
